import { useState } from "react";
import { Clock, Mail, MapPin } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useLang } from "@/context/LanguageContext";
import { Reveal, Overline } from "@/components/Reveal";
import { CONTACT_EMAIL } from "@/data/events";

export default function Contacto() {
  const { t, lang } = useLang();
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });

  const submit = (e) => {
    e.preventDefault();
    const subject = encodeURIComponent(`Contacto web: ${form.name}`);
    const body = encodeURIComponent(
      `Nombre / Name: ${form.name}\nEmail: ${form.email}\n` +
      `Teléfono / Phone: ${form.phone || "-"}\n\n${form.message}`
    );
    window.location.href = `mailto:${CONTACT_EMAIL}?subject=${subject}&body=${body}`;
    toast.success(t.contacto.successTitle, { description: t.contacto.mailNote });
    setForm({ name: "", email: "", phone: "", message: "" });
  };

  const inputCls = "bg-transparent border-0 border-b border-white/20 rounded-none px-0 focus-visible:ring-0 focus-visible:border-ember placeholder:text-white/40";

  return (
    <div data-testid="contacto-page">
      <section className="pt-40 pb-20 max-w-7xl mx-auto px-6 md:px-10">
        <Reveal>
          <Overline>{t.contacto.overline}</Overline>
          <h1 className="font-display text-5xl md:text-7xl font-light tracking-tight mt-6">
            {t.contacto.title}
          </h1>
          <p className="text-white/60 text-lg mt-8 max-w-xl leading-relaxed">{t.contacto.sub}</p>
        </Reveal>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-10 pb-24 md:pb-32 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <Reveal className="lg:col-span-5">
          <div className="flex flex-col gap-10">
            <div className="border-t border-white/15 pt-8">
              <p className="text-xs uppercase tracking-[0.25em] text-sand mb-4 flex items-center gap-2">
                <MapPin size={14} className="text-ember" /> {t.contacto.addressLabel}
              </p>
              <p className="font-display text-3xl font-light" data-testid="contact-address">
                91 Oak Creek Dr<br />Kaufman, TX 75142
              </p>
            </div>
            <div className="border-t border-white/15 pt-8">
              <p className="text-xs uppercase tracking-[0.25em] text-sand mb-4 flex items-center gap-2">
                <Clock size={14} className="text-ember" /> {t.contacto.hoursLabel}
              </p>
              <p className="text-white/70">{t.footer.services}</p>
            </div>
            <div className="border-t border-white/15 pt-8">
              <p className="text-xs uppercase tracking-[0.25em] text-sand mb-4 flex items-center gap-2">
                <Mail size={14} className="text-ember" /> {t.contacto.emailLabel}
              </p>
              <p className="text-white/70">{t.footer.email}</p>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.1} className="lg:col-span-7">
          <form onSubmit={submit} className="border border-white/10 bg-coal p-10 md:p-14 flex flex-col gap-8" data-testid="contact-form">
            <Input
              required
              data-testid="contact-form-name"
              placeholder={t.contacto.form.name}
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className={inputCls}
            />
            <Input
              required
              type="email"
              data-testid="contact-form-email"
              placeholder={t.contacto.form.email}
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className={inputCls}
            />
            <Input
              data-testid="contact-form-phone"
              placeholder={t.contacto.form.phone}
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              className={inputCls}
            />
            <Textarea
              required
              rows={5}
              data-testid="contact-form-message"
              placeholder={t.contacto.form.message}
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              className={`${inputCls} resize-none`}
            />
            <button
              type="submit"
              data-testid="contact-form-submit"
              className="self-start bg-ember text-black text-xs uppercase tracking-[0.2em] font-bold rounded-full px-10 py-4 hover:bg-white transition-colors duration-300"
            >
              {t.contacto.form.submit}
            </button>
          </form>
        </Reveal>
      </section>

      <section className="border-t border-white/10" data-testid="map-section">
        <iframe
          title="Movimiento Church — 91 Oak Creek Dr, Kaufman, TX 75142"
          src="https://www.google.com/maps?q=91+Oak+Creek+Dr,+Kaufman,+TX+75142&output=embed"
          className="w-full h-96 map-frame"
          loading="lazy"
        />
      </section>
    </div>
  );
}
